import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '@/config/environment';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Divider,
  Slider,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  LocationOn,
  Visibility,
  VisibilityOff,
  History,
  DirectionsCar,
  Person,
  DriveEta,
  MonetizationOn,
  Schedule,
  Percent,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import rentalSettingsService, { AddressReleaseLog } from '@/services/rentalSettingsService';
import {
  getRentalSettings,
  updateRentalSettings,
  validateRentalSettings,
  DEFAULT_RENTAL_SETTINGS,
  type RentalSettings,
  getBookingPolicies,
  updateBookingPolicies,
  DEFAULT_BOOKING_POLICIES,
  getHireStandards,
  updateHireStandards,
  DEFAULT_HIRE_STANDARDS,
  getBookingApprovalTimeout,
  updateBookingApprovalTimeout,
  validateBookingApprovalTimeout,
  DEFAULT_BOOKING_APPROVAL_TIMEOUT,
} from '@/services/settingsService';
import { driverSettingsService } from '@/services/driverSettingsService';
import { driverPricingService } from '@/services/driverPricingService';

const RentalSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<RentalSettings>(DEFAULT_RENTAL_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Service-specific settings
  const [enableServiceSpecific, setEnableServiceSpecific] = useState(false);
  const [selfDriveHours, setSelfDriveHours] = useState(2);
  const [driverHireHours, setDriverHireHours] = useState(1);
  const [withDriverHours, setWithDriverHours] = useState(3);

  // History
  const [historyLogs, setHistoryLogs] = useState<AddressReleaseLog[]>([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Migration
  const [migratingDatabase, setMigratingDatabase] = useState(false);

  // Driver Timeout Settings
  const [hourlyTimeoutMinutes, setHourlyTimeoutMinutes] = useState(60);
  const [dailyTimeoutMinutes, setDailyTimeoutMinutes] = useState(120);
  const [savingTimeout, setSavingTimeout] = useState(false);

  // Booking Policies
  const [modificationFee, setModificationFee] = useState(2500);
  const [minimumModificationHours, setMinimumModificationHours] = useState(2);
  const [cancellationPolicies, setCancellationPolicies] = useState({
    free: { hoursThreshold: 48, feePercentage: 0, description: 'Free cancellation' },
    low_fee: { hoursThreshold: 24, feePercentage: 10, description: 'Low fee (10%)' },
    medium_fee: { hoursThreshold: 6, feePercentage: 25, description: 'Medium fee (25%)' },
    high_fee: { hoursThreshold: 1, feePercentage: 50, description: 'High fee (50%)' },
    no_refund: { hoursThreshold: 0, feePercentage: 100, description: 'No refund' },
  });

  // Hire Duration Standards
  const [hourlyMinHours, setHourlyMinHours] = useState(4);
  const [hourlyMaxHours, setHourlyMaxHours] = useState(7);
  const [hourlyRequiresWorkingHours, setHourlyRequiresWorkingHours] = useState(true);
  const [dailyMinDays, setDailyMinDays] = useState(1);
  const [dailyMaxDays, setDailyMaxDays] = useState(14);
  const [savingHireStandards, setSavingHireStandards] = useState(false);

  // Driver Pricing Boundaries
  const [driverPricingHourlyMin, setDriverPricingHourlyMin] = useState(1000);
  const [driverPricingHourlyMax, setDriverPricingHourlyMax] = useState(10000);
  const [driverPricingDailyMin, setDriverPricingDailyMin] = useState(15000);
  const [driverPricingDailyMax, setDriverPricingDailyMax] = useState(100000);
  const [savingDriverPricing, setSavingDriverPricing] = useState(false);

  // Booking Approval Timeout
  const [approvalTimeoutHours, setApprovalTimeoutHours] = useState(24);
  const [savingApprovalTimeout, setSavingApprovalTimeout] = useState(false);

  // Preview data
  const examplePickupTime = new Date();
  examplePickupTime.setHours(examplePickupTime.getHours() + 24); // Tomorrow

  useEffect(() => {
    loadSettings();
    loadHistory();
    loadDriverTimeoutSettings();
    loadBookingPolicies();
    loadHireStandards();
    loadDriverPricing();
    loadBookingApprovalTimeout();
  }, []);

  useEffect(() => {
    loadHistory();
  }, [historyPage, historyRowsPerPage]);

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const result = await rentalSettingsService.getAddressReleaseHistory(
        historyPage + 1,
        historyRowsPerPage
      );

      if (result.success && result.data) {
        setHistoryLogs(result.data.logs || []);
        setHistoryTotal(result.data.pagination?.total || 0);
      } else {
        // Set empty state if API fails
        setHistoryLogs([]);
        setHistoryTotal(0);
      }
    } catch (error) {
      console.error('Failed to load history:', error);
      // Set empty state on error
      setHistoryLogs([]);
      setHistoryTotal(0);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const result = await getRentalSettings();

      if (result.success && result.data) {
        setSettings(result.data);

        // Load service-specific settings if they exist
        if (result.data.serviceSpecific) {
          // Use explicit boolean check to handle false values correctly
          const isEnabled = result.data.serviceSpecific.enabled === true;
          setEnableServiceSpecific(isEnabled);
          setSelfDriveHours(result.data.serviceSpecific.selfDrive || 2);
          setDriverHireHours(result.data.serviceSpecific.driverHire || 1);
          setWithDriverHours(result.data.serviceSpecific.withDriver || 3);
        } else {
          // Set defaults when no serviceSpecific data exists
          setEnableServiceSpecific(false);
          setSelfDriveHours(2);
          setDriverHireHours(1);
          setWithDriverHours(3);
        }
      } else {
        // Use defaults if no settings found
        setSettings(DEFAULT_RENTAL_SETTINGS);
      }
    } catch (error) {
      console.error('Error loading rental settings:', error);
      toast.error('Failed to load rental settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate
    const validationErrors = validateRentalSettings(settings);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix the errors before saving');
      return;
    }

    setErrors({});
    setSaving(true);

    try {
      // Include service-specific settings in the save payload
      const settingsToSave = {
        ...settings,
        serviceSpecific: {
          enabled: enableServiceSpecific,
          selfDrive: selfDriveHours,
          driverHire: driverHireHours,
          withDriver: withDriverHours,
        },
      };
      const result = await updateRentalSettings(settingsToSave);

      if (result.success) {
        toast.success('Rental settings saved successfully');
        // Reload settings from backend to confirm persistence
        await loadSettings();
      } else {
        toast.error(result.error || 'Failed to save rental settings');
      }
    } catch (error) {
      console.error('Error saving rental settings:', error);
      toast.error('Failed to save rental settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(DEFAULT_RENTAL_SETTINGS);
    setErrors({});
    toast.success('Reset to default settings');
  };

  const handleRunMigration = async () => {
    if (!confirm('This will add address release columns to the database. Only run this once. Continue?')) {
      return;
    }

    try {
      setMigratingDatabase(true);
      const token = localStorage.getItem('token');
      // API_BASE is now dynamic

      const response = await fetch(`${getApiBaseUrl()}/api/admin/database/migrate-address-release-deposit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Database migration completed successfully!');
      } else {
        const error = await response.json().catch(() => ({ message: 'Migration failed' }));
        toast.error(error.message || 'Failed to run migration');
      }
    } catch (error) {
      console.error('Failed to run migration:', error);
      toast.error('Failed to run database migration');
    } finally {
      setMigratingDatabase(false);
    }
  };

  const loadDriverTimeoutSettings = async () => {
    try {
      const response = await driverSettingsService.getTimeoutSettings();
      if (response.success && response.data) {
        setHourlyTimeoutMinutes(response.data.hourly_timeout_minutes || 60);
        setDailyTimeoutMinutes(response.data.daily_timeout_minutes || 120);
      }
    } catch (error) {
      console.error('Failed to load driver timeout settings:', error);
      // Use defaults on error - don't show error toast as this is not critical
    }
  };

  const handleSaveDriverTimeout = async () => {
    setSavingTimeout(true);
    try {
      const response = await driverSettingsService.updateTimeoutSettings({
        hourly_timeout_minutes: hourlyTimeoutMinutes,
        daily_timeout_minutes: dailyTimeoutMinutes,
      });

      if (response.success) {
        toast.success('Driver timeout settings saved successfully');
      } else {
        toast.error('Failed to save driver timeout settings');
      }
    } catch (error) {
      console.error('Error saving driver timeout settings:', error);
      toast.error('Failed to save driver timeout settings');
    } finally {
      setSavingTimeout(false);
    }
  };

  const updateCancellationPolicy = (tier: string, field: 'hoursThreshold' | 'feePercentage' | 'description', value: number | string) => {
    setCancellationPolicies(prev => ({
      ...prev,
      [tier]: {
        ...prev[tier as keyof typeof prev],
        [field]: value,
      },
    }));
  };

  const loadBookingPolicies = async () => {
    try {
      const result = await getBookingPolicies();
      if (result.success && result.data) {
        setModificationFee(result.data.modificationFee);
        setMinimumModificationHours(result.data.minimumModificationHours);
        setCancellationPolicies(result.data.cancellationPolicies);
      } else {
        // Use defaults if no policies found
        setModificationFee(DEFAULT_BOOKING_POLICIES.modificationFee);
        setMinimumModificationHours(DEFAULT_BOOKING_POLICIES.minimumModificationHours);
        setCancellationPolicies(DEFAULT_BOOKING_POLICIES.cancellationPolicies);
      }
    } catch (error) {
      console.error('Error loading booking policies:', error);
      // Use defaults on error - don't show error toast as this is not critical
      setModificationFee(DEFAULT_BOOKING_POLICIES.modificationFee);
      setMinimumModificationHours(DEFAULT_BOOKING_POLICIES.minimumModificationHours);
      setCancellationPolicies(DEFAULT_BOOKING_POLICIES.cancellationPolicies);
    }
  };

  const handleSaveBookingPolicies = async () => {
    setSaving(true);
    try {
      const policies = {
        modificationFee,
        minimumModificationHours,
        cancellationPolicies,
      };

      const result = await updateBookingPolicies(policies);

      if (result.success) {
        toast.success('Booking policies saved successfully');
      } else {
        toast.error(result.error || 'Failed to save booking policies');
      }
    } catch (error) {
      console.error('Error saving booking policies:', error);
      toast.error('Failed to save booking policies');
    } finally {
      setSaving(false);
    }
  };

  const loadHireStandards = async () => {
    try {
      const result = await getHireStandards();
      if (result.success && result.data) {
        setHourlyMinHours(result.data.hourly.minimum_hours);
        setHourlyMaxHours(result.data.hourly.maximum_hours);
        setHourlyRequiresWorkingHours(result.data.hourly.requires_working_hours);
        setDailyMinDays(result.data.daily.minimum_days);
        setDailyMaxDays(result.data.daily.maximum_days);
      } else {
        // Use defaults if no standards found
        setHourlyMinHours(DEFAULT_HIRE_STANDARDS.hourly.minimum_hours);
        setHourlyMaxHours(DEFAULT_HIRE_STANDARDS.hourly.maximum_hours);
        setHourlyRequiresWorkingHours(DEFAULT_HIRE_STANDARDS.hourly.requires_working_hours);
        setDailyMinDays(DEFAULT_HIRE_STANDARDS.daily.minimum_days);
        setDailyMaxDays(DEFAULT_HIRE_STANDARDS.daily.maximum_days);
      }
    } catch (error) {
      console.error('Error loading hire standards:', error);
      // Use defaults on error
    }
  };

  const handleSaveHireStandards = async () => {
    // Validation
    if (hourlyMinHours < 1 || hourlyMinHours > 24) {
      toast.error('Hourly minimum must be between 1 and 24 hours');
      return;
    }

    if (hourlyMaxHours <= hourlyMinHours || hourlyMaxHours > 24) {
      toast.error('Hourly maximum must be greater than minimum and ≤ 24 hours');
      return;
    }

    if (dailyMinDays < 1 || dailyMinDays > 30) {
      toast.error('Daily minimum must be between 1 and 30 days');
      return;
    }

    if (dailyMaxDays <= dailyMinDays || dailyMaxDays > 30) {
      toast.error('Daily maximum must be greater than minimum and ≤ 30 days');
      return;
    }

    setSavingHireStandards(true);
    try {
      const standards = {
        hourly: {
          minimum_hours: hourlyMinHours,
          maximum_hours: hourlyMaxHours,
          requires_working_hours: hourlyRequiresWorkingHours,
        },
        daily: {
          minimum_days: dailyMinDays,
          maximum_days: dailyMaxDays,
          requires_working_hours: false, // Always false for daily
        },
      };

      const result = await updateHireStandards(standards);

      if (result.success) {
        toast.success('Hire duration standards saved successfully');
        loadHireStandards(); // Reload
      } else {
        toast.error(result.error || 'Failed to save hire standards');
      }
    } catch (error) {
      console.error('Error saving hire standards:', error);
      toast.error('Failed to save hire standards');
    } finally {
      setSavingHireStandards(false);
    }
  };

  const loadDriverPricing = async () => {
    try {
      const result = await driverPricingService.getBoundaries();
      if (result.success && result.data) {
        setDriverPricingHourlyMin(result.data.hourlyMin);
        setDriverPricingHourlyMax(result.data.hourlyMax);
        setDriverPricingDailyMin(result.data.dailyMin);
        setDriverPricingDailyMax(result.data.dailyMax);
      }
    } catch (error) {
      console.error('Error loading driver pricing boundaries:', error);
    }
  };

  const handleSaveDriverPricing = async () => {
    // Validation
    if (driverPricingHourlyMin < 100) {
      toast.error('Hourly minimum must be at least ₦100');
      return;
    }

    if (driverPricingHourlyMax <= driverPricingHourlyMin) {
      toast.error('Hourly maximum must be greater than minimum');
      return;
    }

    if (driverPricingDailyMin < 1000) {
      toast.error('Daily minimum must be at least ₦1,000');
      return;
    }

    if (driverPricingDailyMax <= driverPricingDailyMin) {
      toast.error('Daily maximum must be greater than minimum');
      return;
    }

    setSavingDriverPricing(true);
    try {
      const result = await driverPricingService.updateBoundaries({
        hourlyMin: driverPricingHourlyMin,
        hourlyMax: driverPricingHourlyMax,
        dailyMin: driverPricingDailyMin,
        dailyMax: driverPricingDailyMax,
        currency: 'NGN'
      });

      if (result.success) {
        toast.success('Driver pricing boundaries saved successfully');
        loadDriverPricing(); // Reload
      } else {
        toast.error(result.error || 'Failed to save driver pricing boundaries');
      }
    } catch (error) {
      console.error('Error saving driver pricing boundaries:', error);
      toast.error('Failed to save driver pricing boundaries');
    } finally {
      setSavingDriverPricing(false);
    }
  };

  const loadBookingApprovalTimeout = async () => {
    try {
      const result = await getBookingApprovalTimeout();
      if (result.success && result.data) {
        setApprovalTimeoutHours(result.data.timeout_hours);
      } else {
        // Use default if no settings found
        setApprovalTimeoutHours(DEFAULT_BOOKING_APPROVAL_TIMEOUT.timeout_hours);
      }
    } catch (error) {
      console.error('Error loading booking approval timeout:', error);
      // Use default on error
      setApprovalTimeoutHours(DEFAULT_BOOKING_APPROVAL_TIMEOUT.timeout_hours);
    }
  };

  const handleSaveBookingApprovalTimeout = async () => {
    // Validation
    const validationError = validateBookingApprovalTimeout(approvalTimeoutHours);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSavingApprovalTimeout(true);
    try {
      const result = await updateBookingApprovalTimeout(approvalTimeoutHours);

      if (result.success) {
        toast.success(result.message || 'Booking approval timeout saved successfully');
        loadBookingApprovalTimeout(); // Reload
      } else {
        toast.error(result.error || 'Failed to save booking approval timeout');
      }
    } catch (error) {
      console.error('Error saving booking approval timeout:', error);
      toast.error('Failed to save booking approval timeout');
    } finally {
      setSavingApprovalTimeout(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            Rental Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure address release timing and security settings
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="warning"
          startIcon={migratingDatabase ? <CircularProgress size={16} /> : <InfoIcon />}
          onClick={handleRunMigration}
          disabled={migratingDatabase}
          size="small"
        >
          {migratingDatabase ? 'Running Migration...' : 'Run Database Migration'}
        </Button>
      </Box>

      {/* Migration Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight="medium" gutterBottom>
          ⚠️ First-Time Setup Required
        </Typography>
        <Typography variant="body2">
          Before enabling address release, click "Run Database Migration" to add required database columns.
          This only needs to be done <strong>once</strong> in production.
        </Typography>
      </Alert>

      {/* Security & Privacy Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon fontSize="small" />
            Security & Privacy
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Enable/Disable Address Release */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enableAddressRelease}
                  onChange={(e) =>
                    setSettings({ ...settings, enableAddressRelease: e.target.checked })
                  }
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Enable Address Release Delay
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    For security, hide sensitive addresses until X hours before booking starts
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Address Release Hours */}
          {settings.enableAddressRelease && (
            <>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body1" fontWeight="medium">
                    Release Address Before Booking Start
                  </Typography>
                  <Tooltip title="Addresses will be revealed X hours before the rental/hire starts. Recommended: 1-2 hours.">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>

                <TextField
                  type="number"
                  value={settings.addressReleaseHours}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    setSettings({ ...settings, addressReleaseHours: value });
                    // Clear error on change
                    if (errors.addressReleaseHours) {
                      setErrors({ ...errors, addressReleaseHours: '' });
                    }
                  }}
                  inputProps={{
                    min: 0,
                    max: 24,
                    step: 0.5,
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                  }}
                  error={!!errors.addressReleaseHours}
                  helperText={errors.addressReleaseHours || 'Minimum: 0 hours (immediate) • Maximum: 24 hours'}
                  fullWidth
                  sx={{ mb: 2 }}
                />

                {/* Slider for visual selection */}
                <Box sx={{ px: 1 }}>
                  <Slider
                    value={settings.addressReleaseHours}
                    onChange={(_, value) =>
                      setSettings({ ...settings, addressReleaseHours: value as number })
                    }
                    min={0}
                    max={24}
                    step={0.5}
                    marks={[
                      { value: 0, label: '0h (Immediate)' },
                      { value: 6, label: '6h' },
                      { value: 12, label: '12h' },
                      { value: 18, label: '18h' },
                      { value: 24, label: '24h' },
                    ]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}h`}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Info Box */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: 'info.lighter',
                  border: '1px solid',
                  borderColor: 'info.light',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <InfoIcon color="info" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight="medium">
                    How Address Release Works
                  </Typography>
                </Box>
                <Box component="ul" sx={{ mt: 1, pl: 3, mb: 0 }}>
                  <li>
                    <Typography variant="body2">
                      <strong>Self-Drive Rentals:</strong> Pickup address hidden from renter until release time
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      <strong>Driver Hire:</strong> Meetup address hidden from renter until release time
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      <strong>Car + Driver:</strong> Destination address hidden from host until release time
                    </Typography>
                  </li>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  This prevents early scouting of vehicle locations and protects privacy for both hosts and renters.
                </Typography>
              </Paper>

              <Divider sx={{ my: 3 }} />

              {/* Service-Specific Settings */}
              <FormControlLabel
                control={
                  <Switch
                    checked={enableServiceSpecific}
                    onChange={(e) => setEnableServiceSpecific(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      Enable Service-Specific Settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Set different release times for each service type
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              {enableServiceSpecific && (
                <Grid container spacing={2}>
                  {/* Self-Drive */}
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#e3f2fd', border: '1px solid', borderColor: 'primary.light' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <DirectionsCar sx={{ color: 'primary.main' }} />
                        <Typography variant="subtitle2" fontWeight="600">Self-Drive Rentals</Typography>
                      </Box>
                      <Slider
                        value={selfDriveHours}
                        onChange={(_, value) => setSelfDriveHours(value as number)}
                        min={0.5}
                        max={24}
                        step={0.5}
                        marks={[
                          { value: 0.5, label: '30min' },
                          { value: 12, label: '12h' },
                          { value: 24, label: '24h' },
                        ]}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value}h`}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                        {selfDriveHours} hours before pickup
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Driver Hire */}
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e9', border: '1px solid', borderColor: 'success.light' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Person sx={{ color: 'success.main' }} />
                        <Typography variant="subtitle2" fontWeight="600">Driver Hire Only</Typography>
                      </Box>
                      <Slider
                        value={driverHireHours}
                        onChange={(_, value) => setDriverHireHours(value as number)}
                        min={0.5}
                        max={24}
                        step={0.5}
                        marks={[
                          { value: 0.5, label: '30min' },
                          { value: 12, label: '12h' },
                          { value: 24, label: '24h' },
                        ]}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value}h`}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                        {driverHireHours} hours before start
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Car + Driver */}
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff3e0', border: '1px solid', borderColor: 'warning.light' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <DriveEta sx={{ color: 'warning.main' }} />
                        <Typography variant="subtitle2" fontWeight="600">Car with Driver</Typography>
                      </Box>
                      <Slider
                        value={withDriverHours}
                        onChange={(_, value) => setWithDriverHours(value as number)}
                        min={0.5}
                        max={24}
                        step={0.5}
                        marks={[
                          { value: 0.5, label: '30min' },
                          { value: 12, label: '12h' },
                          { value: 24, label: '24h' },
                        ]}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value}h`}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                        {withDriverHours} hours before start
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Real-Time Preview */}
      {settings.enableAddressRelease && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Visibility fontSize="small" />
              Live Preview
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={3}>
              {/* Before Release */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <VisibilityOff sx={{ color: 'error.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight="600">
                      Before Release (Hidden)
                    </Typography>
                  </Box>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: '#ffebee', border: '1px solid', borderColor: 'error.light' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Address Hidden
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn sx={{ color: 'error.main', fontSize: 18 }} />
                      <Typography variant="body2" fontWeight="500">
                        Lekki Phase 1, Lagos
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Releases in: {rentalSettingsService.formatTimeUntilRelease(examplePickupTime, settings.addressReleaseHours)}
                    </Typography>
                  </Paper>
                </Box>
              </Grid>

              {/* After Release */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Visibility sx={{ color: 'success.main', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight="600">
                      After Release (Revealed)
                    </Typography>
                  </Box>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e9', border: '1px solid', borderColor: 'success.light' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Full Address Revealed
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn sx={{ color: 'success.main', fontSize: 18 }} />
                      <Typography variant="body2" fontWeight="500">
                        123 Ocean View Drive, Lekki Phase 1, Lagos 105102
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Released {settings.addressReleaseHours}h before pickup
                    </Typography>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Address Release History */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History fontSize="small" />
            Address Release History
          </Typography>

          <Divider sx={{ my: 2 }} />

          {loadingHistory ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : historyLogs.length > 0 ? (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Booking</TableCell>
                      <TableCell>Service Type</TableCell>
                      <TableCell>Renter</TableCell>
                      <TableCell>Address Released</TableCell>
                      <TableCell>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600" color="primary.main">
                            #{log.bookingNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={log.serviceType} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{log.renterName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2">{log.address}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(log.releasedAt).toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={historyTotal}
                page={historyPage}
                onPageChange={(_, newPage) => setHistoryPage(newPage)}
                rowsPerPage={historyRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setHistoryRowsPerPage(parseInt(e.target.value, 10));
                  setHistoryPage(0);
                }}
              />
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <History sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No Release History Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Address releases will appear here once bookings reach their release time
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Driver Acceptance Timeout Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DriveEta fontSize="small" />
            Driver Acceptance Timeout
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure how long drivers have to accept hire requests before they're automatically cancelled
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            {/* Hourly Bookings Timeout */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                  Hourly Bookings Timeout
                </Typography>
                <TextField
                  type="number"
                  value={hourlyTimeoutMinutes}
                  onChange={(e) => setHourlyTimeoutMinutes(Math.max(15, Math.min(240, parseInt(e.target.value) || 60)))}
                  fullWidth
                  InputProps={{
                    endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                  }}
                  inputProps={{
                    min: 15,
                    max: 240,
                    step: 15,
                  }}
                  sx={{ mt: 2 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Recommended: 60 minutes (Range: 15-240 minutes)
                </Typography>
              </Paper>
            </Grid>

            {/* Daily Bookings Timeout */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                  Daily Bookings Timeout
                </Typography>
                <TextField
                  type="number"
                  value={dailyTimeoutMinutes}
                  onChange={(e) => setDailyTimeoutMinutes(Math.max(30, Math.min(480, parseInt(e.target.value) || 120)))}
                  fullWidth
                  InputProps={{
                    endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                  }}
                  inputProps={{
                    min: 30,
                    max: 480,
                    step: 30,
                  }}
                  sx={{ mt: 2 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Recommended: 120 minutes (Range: 30-480 minutes)
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Timeout Behavior:</strong> When a driver doesn't accept a request within the timeout period,
              the booking is automatically cancelled and the renter is notified. The driver's 'missed requests'
              statistic is incremented.
            </Typography>
          </Alert>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveDriverTimeout}
              disabled={savingTimeout}
              startIcon={savingTimeout ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {savingTimeout ? 'Saving...' : 'Save Timeout Settings'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Booking Policies */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule fontSize="small" />
            Booking Policies
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure modification fees, minimum hours, and cancellation policies for bookings
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* General Booking Settings */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Modification Fee"
                type="number"
                value={modificationFee}
                onChange={(e) => setModificationFee(Number(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                }}
                helperText="Fee charged for any booking modification"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Minimum Modification Hours"
                type="number"
                value={minimumModificationHours}
                onChange={(e) => setMinimumModificationHours(Number(e.target.value))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                }}
                helperText="Minimum hours before booking to allow modifications"
              />
            </Grid>
          </Grid>

          {/* Cancellation Policies Table */}
          <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ mt: 3, mb: 2 }}>
            Cancellation Fee Tiers
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Policy Tier</TableCell>
                  <TableCell>Hours Before Pickup</TableCell>
                  <TableCell>Fee Percentage</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(cancellationPolicies).map(([tier, policy]) => (
                  <TableRow key={tier}>
                    <TableCell sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                      {tier.replace('_', ' ')}
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={policy.hoursThreshold}
                        onChange={(e) => updateCancellationPolicy(tier, 'hoursThreshold', Number(e.target.value))}
                        size="small"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">hrs</InputAdornment>,
                        }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={policy.feePercentage}
                        onChange={(e) => updateCancellationPolicy(tier, 'feePercentage', Number(e.target.value))}
                        size="small"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={policy.description}
                        onChange={(e) => updateCancellationPolicy(tier, 'description', e.target.value)}
                        size="small"
                        fullWidth
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>How it works:</strong> Cancellation fees are calculated based on how many hours before the pickup time
              the user cancels. For example, if canceled 30 hours before pickup, it falls under the "Free cancellation" tier (48+ hours).
            </Typography>
          </Alert>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveBookingPolicies}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {saving ? 'Saving...' : 'Save Booking Policies'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Booking Approval Timeout */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule fontSize="small" />
            Booking Approval Timeout
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            How long should hosts have to approve booking requests before automatic expiration and refund?
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Timeout (hours)"
                value={approvalTimeoutHours}
                onChange={(e) => setApprovalTimeoutHours(Number(e.target.value))}
                inputProps={{ min: 1, max: 72, step: 1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                }}
                helperText="Valid range: 1-72 hours"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: 'info.lighter',
                  border: '1px solid',
                  borderColor: 'info.light',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <InfoIcon color="info" fontSize="small" />
                    <Typography variant="subtitle2" fontWeight="medium">
                      Current Setting
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    Bookings expire after <strong>{approvalTimeoutHours} hours</strong> if not approved by the host
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>How it works:</strong> When a host doesn't approve a booking request within the timeout period,
              the booking is automatically cancelled, the customer is refunded, and the host's statistics are updated.
              This ensures customers aren't left waiting indefinitely.
            </Typography>
          </Alert>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveBookingApprovalTimeout}
              disabled={savingApprovalTimeout}
              startIcon={savingApprovalTimeout ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {savingApprovalTimeout ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Hire Duration Standards */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule />
            Hire Duration Standards
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure platform-wide rules for hourly vs daily driver hire bookings. These settings control booking duration limits and working hours requirements.
          </Typography>

          {/* Hourly Hire Settings */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              ⏱️ Hourly Hire Settings
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Hours Required"
                  value={hourlyMinHours}
                  onChange={(e) => setHourlyMinHours(Number(e.target.value))}
                  inputProps={{ min: 1, max: 24 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                  }}
                  helperText="Minimum booking duration (1-24 hours)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Maximum Hours Allowed"
                  value={hourlyMaxHours}
                  onChange={(e) => setHourlyMaxHours(Number(e.target.value))}
                  inputProps={{ min: 1, max: 24 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                  }}
                  helperText="Maximum hourly booking (must be > minimum and ≤ 24)"
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={hourlyRequiresWorkingHours}
                  onChange={(e) => setHourlyRequiresWorkingHours(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Require bookings to fit within driver's working hours
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    When enabled, hourly bookings must start and end within the driver's designated working hours
                  </Typography>
                </Box>
              }
              sx={{ mb: 2 }}
            />

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Hourly bookings</strong> are for short-term hire with specific time requirements.
              </Typography>
              <Typography variant="body2" component="div">
                <strong>Example:</strong>
                <ul style={{ margin: '8px 0', paddingLeft: '24px' }}>
                  <li>Driver works: 9:00 AM - 8:00 PM</li>
                  <li>✅ Valid booking: 10:00 AM - 2:00 PM (4 hours)</li>
                  <li>❌ Invalid booking: 7:00 AM - 11:00 AM (4 hours) - starts before driver's work hours</li>
                </ul>
              </Typography>
            </Alert>
          </Box>

          {/* Daily Hire Settings */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              📅 Daily Hire Settings
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Days Required"
                  value={dailyMinDays}
                  onChange={(e) => setDailyMinDays(Number(e.target.value))}
                  inputProps={{ min: 1, max: 30 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">days</InputAdornment>,
                  }}
                  helperText="Minimum multi-day hire (1-30 days)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Maximum Days Allowed"
                  value={dailyMaxDays}
                  onChange={(e) => setDailyMaxDays(Number(e.target.value))}
                  inputProps={{ min: 1, max: 30 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">days</InputAdornment>,
                  }}
                  helperText="Maximum for driver safety (must be > minimum and ≤ 30)"
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={<Switch checked={false} disabled />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium" color="text.disabled">
                    Require bookings to fit within driver's working hours
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    (Disabled - daily hire has flexible timing)
                  </Typography>
                </Box>
              }
              sx={{ mb: 2 }}
            />

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Daily bookings</strong> are for multi-day hire. Duration is calculated by calendar days (Dec 3 10am to Dec 5 2pm = 2 days). Start time is flexible - driver manages their schedule across the rental period.
              </Typography>
              <Typography variant="body2" component="div">
                <strong>Example:</strong>
                <ul style={{ margin: '8px 0', paddingLeft: '24px' }}>
                  <li>Driver available: Monday - Friday</li>
                  <li>✅ Valid: Dec 3 10am - Dec 4 2pm (2 days)</li>
                  <li>✅ Valid: Dec 23 8am - Dec 26 10am (3 days)</li>
                  <li>❌ Invalid: Starting on Saturday (driver's start day must be a working day)</li>
                </ul>
              </Typography>
            </Alert>
          </Box>

          {/* Current Rules Summary */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'primary.lighter',
              border: '1px solid',
              borderColor: 'primary.light',
              mb: 3,
            }}
          >
            <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
              Current Platform Rules Summary
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Hourly Hire:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {hourlyMinHours}-{hourlyMaxHours} hours
                </Typography>
                <Chip label={hourlyRequiresWorkingHours ? "Strict working hours" : "Flexible timing"} size="small" sx={{ mt: 0.5 }} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Daily Hire:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {dailyMinDays}-{dailyMaxDays} days
                </Typography>
                <Chip label="Flexible timing, multi-day" size="small" sx={{ mt: 0.5 }} />
              </Box>
            </Box>
          </Paper>

          {/* Save Button */}
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveHireStandards}
              disabled={savingHireStandards}
              startIcon={savingHireStandards ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {savingHireStandards ? 'Saving...' : 'Save Hire Standards'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Driver Pricing Boundaries */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonetizationOn />
            Driver Pricing Boundaries
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set minimum and maximum pricing limits for driver hire rates. These boundaries ensure fair pricing across the platform and sync automatically to the mobile app.
          </Typography>

          {/* Hourly Rate Boundaries */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              ⏱️ Hourly Rate Boundaries
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Hourly Rate"
                  value={driverPricingHourlyMin}
                  onChange={(e) => setDriverPricingHourlyMin(Number(e.target.value))}
                  inputProps={{ min: 100, step: 100 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  }}
                  helperText="Minimum hourly rate drivers can set (e.g., ₦1,000)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Maximum Hourly Rate"
                  value={driverPricingHourlyMax}
                  onChange={(e) => setDriverPricingHourlyMax(Number(e.target.value))}
                  inputProps={{ min: 100, step: 100 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  }}
                  helperText="Maximum hourly rate drivers can set (e.g., ₦10,000)"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Daily Rate Boundaries */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              📅 Daily Rate Boundaries
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Daily Rate"
                  value={driverPricingDailyMin}
                  onChange={(e) => setDriverPricingDailyMin(Number(e.target.value))}
                  inputProps={{ min: 1000, step: 1000 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  }}
                  helperText="Minimum daily rate drivers can set (e.g., ₦15,000)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Maximum Daily Rate"
                  value={driverPricingDailyMax}
                  onChange={(e) => setDriverPricingDailyMax(Number(e.target.value))}
                  inputProps={{ min: 1000, step: 1000 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  }}
                  helperText="Maximum daily rate drivers can set (e.g., ₦100,000)"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Current Boundaries Summary */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'success.lighter',
              border: '1px solid',
              borderColor: 'success.light',
              mb: 3,
            }}
          >
            <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
              Current Pricing Boundaries
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Hourly Rate Range:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  ₦{(driverPricingHourlyMin || 0).toLocaleString()} - ₦{(driverPricingHourlyMax || 0).toLocaleString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Daily Rate Range:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  ₦{(driverPricingDailyMin || 0).toLocaleString()} - ₦{(driverPricingDailyMax || 0).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Note:</strong> These pricing boundaries automatically sync to the mobile app. Drivers will see these ranges when setting their hourly and daily rates in the app.
            </Typography>
          </Alert>

          {/* Save Button */}
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveDriverPricing}
              disabled={savingDriverPricing}
              startIcon={savingDriverPricing ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {savingDriverPricing ? 'Saving...' : 'Save Driver Pricing Boundaries'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleReset}
          disabled={saving}
          startIcon={<ResetIcon />}
        >
          Reset to Default
        </Button>
      </Box>
    </Box>
  );
};

export default RentalSettingsPage;
